import type { CashCountBreakdown } from './denominations';

export type CashMovementType =
  | 'opening'
  | 'sale_receipt'
  | 'payment_receipt'
  | 'manual_deposit'
  | 'withdrawal'
  | 'closing'
  /** Constat de comptage physique. Montant = ce qui a été compté ; AUCUN effet
   *  sur le solde théorique (c'est une observation, pas un flux). */
  | 'count'
  /** Régularisation qui réaligne le théorique sur le réel. Montant SIGNÉ :
   *  négatif = manquant constaté. Seul type dont le montant peut être < 0. */
  | 'adjustment';

export type WithdrawalCategory =
  | 'bank_deposit'
  | 'supplier_payment'
  | 'salary_advance'
  | 'small_purchase'
  | 'other';

export type DepositCategory = 'cash_injection' | 'other';

/** Comment une session a été ouverte / fermée. */
export type SessionMode = 'manual' | 'auto';

/** Politique de report du fond de caisse d'un jour sur l'autre. */
export type FloatCarryMode = 'carry' | 'fixed' | 'zero';

/**
 * Réglages de caisse d'un tenant. Miroir de la table `caisse_settings` ; les
 * valeurs par défaut ici DOIVENT rester alignées sur les DEFAULT SQL pour
 * qu'une ligne manquante se comporte comme une ligne fraîche.
 */
export interface CaisseSettings {
  sessionMode: 'auto' | 'manual';
  /** Bascule de journée commerciale, en minutes après minuit (0 = minuit). */
  dayCutoffMinutes: number;
  floatCarryMode: FloatCarryMode;
  fixedFloat: number;
  countReminder: boolean;
  autoAttachCashPayments: boolean;
}

export const DEFAULT_CAISSE_SETTINGS: CaisseSettings = {
  sessionMode: 'auto',
  dayCutoffMinutes: 0,
  floatCarryMode: 'carry',
  fixedFloat: 0,
  countReminder: true,
  autoAttachCashPayments: true,
};

/** Instantané statistique dénormalisé, calculé par le domaine et posé sur la
 *  session. Permet une statistique annuelle sans réagréger tous les paiements. */
export interface CashSessionStats {
  totalReceipts: number;
  cashReceipts: number;
  ticketCount: number;
  salesAmount: number;
  profit: number;
  byMethod: Record<string, { total: number; count: number }>;
  /**
   * Encaissements ventilés par heure de la journée, clés `'0'`…`'23'`.
   *
   * OPTIONNEL À DESSEIN : les instantanés figés avant l'arrivée de la carte
   * d'affluence n'en portent pas. `undefined` veut dire « on ne sait pas à
   * quelle heure », jamais « personne n'est venu » — l'écran d'affluence
   * compte ces journées à part au lieu de les peindre en zéro.
   *
   * L'heure vient de l'instant d'ENREGISTREMENT du règlement, pas de
   * `payment_date` qui est une DATE sans heure en base.
   */
  byHour?: Record<string, { total: number; count: number }>;
  computedAt: string;
}

export interface CashSession {
  id: string;
  number: string;
  bankAccountId: string;
  openedBy: string;
  openedAt: string;
  openingBalance: number;
  /** Détail du fond d'ouverture par coupures. `null` = fond saisi en montant
   *  global, sans compter le tiroir pièce par pièce. */
  openingBreakdown: CashCountBreakdown | null;
  closedBy: string | null;
  closedAt: string | null;
  expectedBalance: number | null;
  /** Montant physiquement compté. `null` = JAMAIS compté (clôture automatique).
   *  Ne jamais confondre avec 0 : `difference` est alors null, pas 0. */
  countedBalance: number | null;
  /** Détail du DERNIER comptage par coupures. `null` = comptage saisi en montant
   *  global (ou tiroir jamais compté). Quand il existe, il somme EXACTEMENT à
   *  `countedBalance` — l'adapter recalcule le montant depuis le détail. */
  countedBreakdown: CashCountBreakdown | null;
  difference: number | null;
  isClosed: boolean;
  notes: string | null;
  /** Journée commerciale de rattachement, `YYYY-MM-DD`. */
  sessionDate: string;
  openMode: SessionMode;
  closeMode: SessionMode | null;
  countedAt: string | null;
  countedBy: string | null;
  stats: CashSessionStats | null;
  createdAt: string;
  updatedAt: string;
}

export interface CashMovement {
  id: string;
  number: string;
  sessionId: string;
  movementType: CashMovementType;
  category: string | null;
  amount: number;
  description: string | null;
  /**
   * Détail par coupures du comptage qui a produit ce mouvement (`opening`,
   * `count`, `closing`). `null` partout ailleurs — un encaissement de vente n'a
   * pas de détail de tiroir.
   *
   * C'est l'ARCHIVE : `cash_sessions.counted_breakdown` ne garde que le dernier
   * comptage, alors qu'un commerce peut compter son tiroir trois fois dans la
   * journée. Le journal, lui, garde les trois.
   */
  breakdown: CashCountBreakdown | null;
  paymentId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CashSessionSummary {
  openingBalance: number;
  totalCashReceipts: number;
  totalDeposits: number;
  totalWithdrawals: number;
  /** Somme des régularisations signées appliquées au tiroir. */
  totalAdjustments: number;
  expectedBalance: number;
}

/** État de comptage d'une session, dérivé — jamais stocké en double. */
export type CountStatus = 'counted' | 'not_counted';

export function countStatus(
  session: Pick<CashSession, 'countedBalance'>,
): CountStatus {
  return session.countedBalance == null ? 'not_counted' : 'counted';
}
